// Single source of truth for the "checkout intent" — what we want to happen
// the next time the user reaches the checkout surface.
//
// Born from the `?add_to_cart=` / `?coupon=` deeplinks: marketing campaigns
// drop users at a URL that pre-stages products and a discount, but the actual
// cart mutations (addToCart, putCoupon) can only run once the user is
// authenticated and the order exists. The intent bridges that gap — the URL
// capture in `main.tsx` persists it, the executor inside CheckoutV2 consumes
// and clears it.
//
// URL shape:
//   ?add_to_cart=<pv>                  product version id to add (repeatable)
//   ?year_<pv>=<year>                  year metadata for that pv (annual products)
//   ?asat_<pv>=<text>                  asat_information.message for that pv
//   ?coupon=<code>                     coupon code to apply (global)
//
// Per-product extras are scoped by suffix so a multi-item link like
// `?add_to_cart=130&year_130=2026&add_to_cart=31&asat_31=…` reads unambiguously.
//
// The intent stores RAW data per pv (productVersionId + optional year/message);
// it does NOT decide the product KIND. The kind (subscription / annual / asat /
// regularization) and the resulting payload are resolved at consumption against
// the live catalog — see `resolveCheckoutPayload` in `useApplyCheckoutIntent`.
// This keeps the catalog (backend) as the single source of truth for what each
// product_version_id is, instead of inferring it from URL suffixes.

import SESSION_STORAGE_KEY from '../constants/sessionStorageKey';
import {
	DEEPLINK_PARAMS,
	productExtraParam,
	PRODUCT_EXTRA_PARAM_RE,
} from '../constants/deeplinkParams';
import { isSessionStorageEnabled } from '../utils/general.util';
import type { CartSource } from './cartSource';

// ─── Item shape ─────────────────────────────────────────────────────────

/** Raw per-product intent: a `product_version_id` plus the optional extras the
 *  URL carried. Which of these the backend actually needs depends on the
 *  product's catalog kind, resolved at consumption. */
export type CheckoutIntentItem = {
	productVersionId: number;
	/** `?year_<pv>` — used by annual / back-annual products. */
	year?: number;
	/** `?asat_<pv>` — used by ASAT trámites (asat_information.message). */
	message?: string;
	source?: CartSource;
};

export type CheckoutIntent = {
	items: CheckoutIntentItem[];
	couponCode: string | null;
};

// ─── Storage helpers ────────────────────────────────────────────────────

const EMPTY_INTENT: CheckoutIntent = {
	items: [],
	couponCode: null,
};

export const isEmptyIntent = (intent: CheckoutIntent): boolean =>
	intent.items.length === 0 && !intent.couponCode;

// A real declaration year — guards against `0`, empty-string-coerced `0`, and
// out-of-range junk.
export const isValidYear = (value: unknown): value is number =>
	typeof value === 'number' &&
	Number.isInteger(value) &&
	value >= 2000 &&
	value <= 2100;

const ASAT_MESSAGE_MAX_LENGTH = 1024;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;

// Free-text asat message from an external deeplink — strip control chars
// (`\x00` & friends), trim, and cap so a malformed link can't bloat storage
// or the backend payload.
export const sanitizeAsatMessage = (value: unknown): string | null => {
	if (typeof value !== 'string') return null;
	const cleaned = value
		.replace(CONTROL_CHARS_RE, '')
		.trim()
		.slice(0, ASAT_MESSAGE_MAX_LENGTH);
	return cleaned.length > 0 ? cleaned : null;
};

const sanitizeItem = (raw: unknown): CheckoutIntentItem | null => {
	if (typeof raw !== 'object' || raw === null) return null;
	const obj = raw as Partial<CheckoutIntentItem>;
	const pv = obj.productVersionId;
	if (typeof pv !== 'number' || !Number.isInteger(pv) || pv <= 0) return null;

	const item: CheckoutIntentItem = { productVersionId: pv };
	if (isValidYear(obj.year)) item.year = obj.year;
	const message = sanitizeAsatMessage(obj.message);
	if (message) item.message = message;
	if (typeof obj.source === 'string' && obj.source) item.source = obj.source;
	return item;
};

export const readCheckoutIntent = (): CheckoutIntent => {
	if (!isSessionStorageEnabled()) return EMPTY_INTENT;
	try {
		const raw = sessionStorage.getItem(SESSION_STORAGE_KEY.CHECKOUT_INTENT);
		if (!raw) return EMPTY_INTENT;
		const parsed = JSON.parse(raw) as Partial<CheckoutIntent>;
		return {
			items: Array.isArray(parsed.items)
				? parsed.items
						.map(sanitizeItem)
						.filter((item): item is CheckoutIntentItem => item !== null)
				: [],
			couponCode:
				typeof parsed.couponCode === 'string' && parsed.couponCode.length > 0
					? parsed.couponCode
					: null,
		};
	} catch {
		return EMPTY_INTENT;
	}
};

export const writeCheckoutIntent = (intent: CheckoutIntent): void => {
	if (!isSessionStorageEnabled()) return;
	if (isEmptyIntent(intent)) {
		clearCheckoutIntent();
		return;
	}
	try {
		sessionStorage.setItem(
			SESSION_STORAGE_KEY.CHECKOUT_INTENT,
			JSON.stringify(intent),
		);
	} catch {
		// Storage quota exceeded or unavailable — silently drop the intent
	}
};

export const clearCheckoutIntent = (): void => {
	if (!isSessionStorageEnabled()) return;
	try {
		sessionStorage.removeItem(SESSION_STORAGE_KEY.CHECKOUT_INTENT);
	} catch {
		// Ignore — storage unavailable
	}
};

export const hasPendingCheckoutIntent = (): boolean =>
	!isEmptyIntent(readCheckoutIntent());

/**
 * Stage a coupon-only intent from an IN-APP surface (promo banners) — the same
 * machinery the `?coupon=` deeplink feeds, so both entry points converge on
 * useApplyCheckoutIntent: when the checkout opens, the coupon attaches to the
 * order and the backend decides whether the cart's plan qualifies. Preserves
 * any staged items.
 */
export const stageCouponCheckoutIntent = (couponCode: string): void => {
	const existing = readCheckoutIntent();
	writeCheckoutIntent({ ...existing, couponCode });
};

// ─── URL capture ────────────────────────────────────────────────────────

// Merge a freshly parsed intent with whatever is already in storage, keying by
// `productVersionId` so back-to-back deeplinks compose. Incoming wins on
// collision (last-touch overrides the stored extras for the same product).
const mergeIntent = (
	existing: CheckoutIntent,
	incoming: CheckoutIntent,
): CheckoutIntent => {
	const byPv = new Map<number, CheckoutIntentItem>();
	for (const item of existing.items) byPv.set(item.productVersionId, item);
	for (const item of incoming.items) byPv.set(item.productVersionId, item);
	return {
		items: Array.from(byPv.values()),
		couponCode: incoming.couponCode ?? existing.couponCode,
	};
};

// Reads the per-product extras present in the URL into a raw item. No kind
// inference — a malformed `year_<pv>` / empty `asat_<pv>` is simply not
// captured; the consumption-time resolver decides what the pv needs.
const parseItemFromUrl = (
	url: URL,
	productVersionId: number,
): CheckoutIntentItem => {
	const item: CheckoutIntentItem = { productVersionId };

	const asatRaw = url.searchParams.get(productExtraParam.asat(productVersionId));
	const message = sanitizeAsatMessage(asatRaw);
	if (message) item.message = message;

	const yearRaw = url.searchParams.get(productExtraParam.year(productVersionId));
	if (yearRaw !== null) {
		const year = Number(yearRaw);
		if (isValidYear(year)) item.year = year;
	}

	return item;
};

export const stageCheckoutIntentFromUrl = (url: URL): CheckoutIntent | null => {
	const productIds = url.searchParams
		.getAll(DEEPLINK_PARAMS.ADD_TO_CART)
		.map((raw) => Number(raw))
		.filter((id) => Number.isInteger(id) && id > 0);
	const uniquePvIds = Array.from(new Set(productIds));
	const couponCode = url.searchParams.get(DEEPLINK_PARAMS.COUPON);

	const items = uniquePvIds.map((id) => parseItemFromUrl(url, id));

	if (items.length === 0 && !couponCode) return null;

	const incoming = { items, couponCode: couponCode ?? null };
	writeCheckoutIntent(mergeIntent(readCheckoutIntent(), incoming));
	return incoming;
};

export const stripCheckoutIntentParams = (url: URL): URL => {
	const clean = new URL(url);
	// Strip every param the parser consumed. add_to_cart / coupon by name;
	// year_* and asat_* dynamically (the suffix is a runtime product id).
	clean.searchParams.delete(DEEPLINK_PARAMS.ADD_TO_CART);
	clean.searchParams.delete(DEEPLINK_PARAMS.COUPON);
	const dynamicKeys: string[] = [];
	clean.searchParams.forEach((_, key) => {
		if (PRODUCT_EXTRA_PARAM_RE.test(key)) dynamicKeys.push(key);
	});
	for (const key of dynamicKeys) clean.searchParams.delete(key);
	return clean;
};

// Captures `?add_to_cart`, `?year_<pv>`, `?asat_<pv>`, and `?coupon` from
// `window.location` and merges them into the stored intent. Designed to run
// once at boot from `main.tsx`, BEFORE React mounts — so the intent is staged
// for both authenticated landings (modal opens as MainLayout mounts) and
// anonymous ones (it survives the redirect through /login).
//
// Strips every consumed param via `history.replaceState` so a refresh after
// consumption doesn't replay the same campaign side effects.
export const captureCheckoutIntentFromUrl = (): void => {
	if (typeof window === 'undefined') return;
	const url = new URL(window.location.href);
	if (!stageCheckoutIntentFromUrl(url)) return;
	window.history.replaceState({}, '', stripCheckoutIntentParams(url).toString());
};
